import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LayoutDashboard,
  Users,
  Car,
  MapPin,
  DollarSign,
  Settings,
  LogOut,
  Menu,
  X,
  Bike,
  Clock,
  Wallet,
  TrendingUp,
  Tag,
  Palette,
  Image,
  Gift,
  Percent,
  FileText,
  UserMinus,
  UserCheck,
  XCircle,
  BarChart2,
  Filter,
  AlertTriangle,
  Flame,
  Bell,
  CreditCard,
  Package,
  Activity,
  Search,
  Building2,
} from "lucide-react";

// Import extracted components
import { DriversManagement } from "./components/DriversManagement";
import { RidesManagement } from "./components/RidesManagement";
import { CityManagement } from "./components/CityManagement";
import { CustomersManagement } from "./components/CustomersManagement";
import { ThemeManagement } from "./components/ThemeManagement";
import { DocumentsManagement } from "./components/DocumentsManagement";
import { BrandingManagement } from "./components/BrandingManagement";
import { PromoCodesManagement } from "./components/PromoCodesManagement";
import { SurgePricingManagement } from "./components/SurgePricingManagement";
import { ReferralManagement } from "./components/ReferralManagement";
import { VehicleSettingsManagement } from "./components/VehicleSettingsManagement";
import { AdminWalletManagement } from "./components/AdminWalletManagement";
import { AdminSettingsManagement } from "./components/AdminSettingsManagement";
import { UsersManagement } from "./components/UsersManagement";
import { CommissionManagement } from "./components/CommissionManagement";
import { CommissionReportDashboard } from "./components/CommissionReportDashboard";
import { CancelledRidesAnalytics } from "./components/CancelledRidesAnalytics";
import { DriversAffectedByCancellations } from "./components/DriversAffectedByCancellations";
import { SubAdminsManagement } from "./components/SubAdminsManagement";
import { CityAdminsManagement } from "./components/CityAdminsManagement";
import { CityAdminEarningsManagement } from "./components/CityAdminEarningsManagement";
import { TrialRibbon } from "./components/TrialRibbon";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { RideHeatMap } from "./components/RideHeatMap";
import { NotificationsManagement } from "./components/NotificationsManagement";
import { PaymentReportsPage } from "./components/PaymentReportsPage";
import { ReportsManagement } from "./components/ReportsManagement";
import RiderSummaryReport from "./components/RiderSummaryReport";
import { FoodDeliveryLedger } from "./components/FoodDeliveryLedger";
import { FoodDriversManagement } from "./components/FoodDriversManagement";
import { DataHealthCheck } from "./components/DataHealthCheck";
import { RiderManagement } from "./components/RiderManagement";
import { CityManagementCRM } from "./components/CityManagementCRM";
import OrderDebugPage from "./components/OrderDebugPage";
import CityOwnershipPage from "./components/CityOwnershipPage";

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("all");
  const [stats, setStats] = useState({
    total_customers: 0,
    total_drivers: 0,
    online_drivers: 0,
    today_rides: 0,
    today_bike_taxi: 0,
    today_food_delivery: 0,
    revenue_today: 0,
    pending_rides: 0,
  });

  useEffect(() => {
    fetchCities();
    fetchStats();
    const path = location.pathname.split("/").pop();
    if (path && path !== "admin") {
      if (path === "cities") setActiveTab("cities");
      else if (path === "drivers") setActiveTab("drivers");
      else if (path === "documents") setActiveTab("documents");
      else if (path === "rides") setActiveTab("rides");
      else if (path === "cancelled-analytics") setActiveTab("cancelled-analytics");
      else if (path === "customers") setActiveTab("customers");
      else if (path === "commission") setActiveTab("commission");
      else if (path === "payment-reports") setActiveTab("payment-reports");
      else if (path === "commission-report") setActiveTab("commission-report");
      else if (path === "rider-summary") setActiveTab("rider-summary");
      else if (path === "promos") setActiveTab("promos");
      else if (path === "surge") setActiveTab("surge");
      else if (path === "referral") setActiveTab("referral");
      else if (path === "theme") setActiveTab("theme");
      else if (path === "branding") setActiveTab("branding");
      else if (path === "wallet") setActiveTab("wallet");
      else if (path === "settings") setActiveTab("settings");
      else if (path === "users") setActiveTab("users");
      else if (path === "vehicle-settings") setActiveTab("vehicle-settings");
      else if (path === "sub-admins") setActiveTab("sub-admins");
      else if (path === "city-admins") setActiveTab("city-admins");
      else if (path === "city-admin-payouts") setActiveTab("city-admin-payouts");
      else if (path === "heatmap") setActiveTab("heatmap");
      else if (path === "notifications") setActiveTab("notifications");
      else if (path === "reports") setActiveTab("reports");
      else if (path === "food-drivers") setActiveTab("food-drivers");
      else if (path === "rider-management") setActiveTab("rider-management");
      else if (path === "city-management") setActiveTab("city-management");
      else if (path === "city-ownership") setActiveTab("city-ownership");
    }
  }, [location.pathname]);

  const fetchCities = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/cities?admin_id=${user.id}`);
      setCities(res.data || []);
    } catch (err) {
      console.error("Failed to fetch cities", err);
    }
  };

  const fetchStats = async (cityId = selectedCity) => {
    if (!user?.id) return;
    try {
      const params = new URLSearchParams({ admin_id: user.id });
      if (cityId && cityId !== "all") {
        params.append("city_id", cityId);
      }
      const res = await axios.get(`${API}/admin/stats?${params.toString()}`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats", err);
    }
  };

  useEffect(() => {
    fetchStats(selectedCity);
  }, [selectedCity]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Base nav items - some are hidden for sub-admins
  const allNavItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "heatmap", label: "Heatmap", icon: Flame },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "cities", label: "Cities", icon: MapPin },
    { id: "city-admins", label: "City Admins", icon: UserCheck },
    { id: "city-admin-payouts", label: "CA Payouts", icon: Wallet },
    { id: "sub-admins", label: "Sub-Admins", icon: UserMinus, adminOnly: true },  // Hidden for sub-admins
    { id: "drivers", label: "Taxi Drivers", icon: Bike },
    { id: "food-drivers", label: "Food Drivers", icon: Package },
    { id: "rider-management", label: "Rider Management", icon: Users },
    { id: "city-management", label: "City Management", icon: MapPin },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "rides", label: "Rides", icon: Car },
    { id: "food-delivery-ledger", label: "Food Delivery", icon: DollarSign },
    { id: "cancelled-analytics", label: "Cancelled Rides", icon: XCircle },
    { id: "affected-drivers", label: "Affected Drivers", icon: AlertTriangle },
    { id: "customers", label: "Customers", icon: Users },
    { id: "commission", label: "Commission", icon: DollarSign },
    { id: "payment-reports", label: "Payment Reports", icon: CreditCard },
    { id: "commission-report", label: "Commission Report", icon: BarChart2 },
    { id: "rider-summary", label: "Rider Summary", icon: Users },
    { id: "reports", label: "Reports", icon: FileText },
    { id: "order-debug", label: "Order Debug", icon: Search },
    { id: "city-ownership", label: "City Ownership", icon: Building2, mainAdminOnly: true },
    { id: "data-health", label: "Data Health", icon: Activity, adminOnly: true },
    { id: "vehicle-settings", label: "Vehicles", icon: Car },
    { id: "promos", label: "Promo Codes", icon: Tag },
    { id: "surge", label: "Surge Pricing", icon: Percent },
    { id: "referral", label: "Referrals", icon: Gift },
    { id: "theme", label: "Theme", icon: Palette },
    { id: "branding", label: "Branding", icon: Image },
    { id: "wallet", label: "Wallet/Subscription", icon: Wallet },
    { id: "settings", label: "Settings", icon: Settings },
  ];
  
  // Filter nav items based on user role - sub-admins can't see adminOnly items
  // mainAdminOnly items only visible to role=admin (Main Admins) or role=super_admin
  const navItems = allNavItems.filter(item => {
    if (item.adminOnly && user?.role === "sub_admin") {
      return false;
    }
    if (item.mainAdminOnly && user?.role !== "admin" && user?.role !== "super_admin") {
      return false;
    }
    return true;
  });

  const renderContent = () => {
    switch (activeTab) {
      case "drivers":
        return <DriversManagement />;
      case "food-drivers":
        return <FoodDriversManagement />;
      case "rider-management":
        return <RiderManagement />;
      case "city-management":
        return <CityManagementCRM />;
      case "documents":
        return <DocumentsManagement />;
      case "rides":
        return <RidesManagement />;
      case "food-delivery-ledger":
        return <FoodDeliveryLedger />;
      case "cities":
        return <CityManagement />;
      case "customers":
        return <CustomersManagement />;
      case "users":
        return <UsersManagement />;
      case "commission":
        return <CommissionManagement />;
      case "payment-reports":
        return <PaymentReportsPage user={user} />;
      case "commission-report":
        return <CommissionReportDashboard />;
      case "rider-summary":
        return <RiderSummaryReport adminId={user?.id} />;
      case "cancelled-analytics":
        return <CancelledRidesAnalytics />;
      case "affected-drivers":
        return <DriversAffectedByCancellations isCityAdmin={false} />;
      case "theme":
        return <ThemeManagement />;
      case "branding":
        return <BrandingManagement />;
      case "promos":
        return <PromoCodesManagement />;
      case "surge":
        return <SurgePricingManagement />;
      case "referral":
        return <ReferralManagement />;
      case "vehicle-settings":
        return <VehicleSettingsManagement />;
      case "wallet":
        return <AdminWalletManagement />;
      case "settings":
        return <AdminSettingsManagement />;
      case "sub-admins":
        return <SubAdminsManagement />;
      case "city-admins":
        return <CityAdminsManagement />;
      case "city-admin-payouts":
        return <CityAdminEarningsManagement />;
      case "heatmap":
        return <RideHeatMap />;
      case "reports":
        return <ReportsManagement />;
      case "order-debug":
        return <OrderDebugPage />;
      case "city-ownership":
        return <CityOwnershipPage user={user} />;
      case "data-health":
        return <DataHealthCheck />;
      case "notifications":
        return (
          <NotificationsManagement 
            adminId={user?.id} 
            adminRole={user?.role}
            assignedCity={user?.assigned_city}
          />
        );
      default:
        return (
          <DashboardOverview 
            stats={stats} 
            setActiveTab={setActiveTab} 
            cities={cities}
            selectedCity={selectedCity}
            setSelectedCity={setSelectedCity}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 bg-card border-b border-border">
        <div className="flex items-center gap-2">
          <Bike className="w-6 h-6 text-primary" />
          <span className="font-bold text-foreground">Admin</span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Impersonation Banner */}
      <ImpersonationBanner />

      {/* Trial Ribbon */}
      <TrialRibbon />

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform lg:transform-none ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="hidden lg:flex items-center gap-2 p-6 border-b border-border">
              <Bike className="w-8 h-8 text-primary" />
              <span className="font-bold text-xl text-foreground">Admin Panel</span>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setSidebarOpen(false);
                    navigate(`/admin/${item.id === "dashboard" ? "" : item.id}`);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    activeTab === item.id
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </nav>

            {/* User Info */}
            <div className="p-4 border-t border-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-medium">
                    {user?.name?.charAt(0) || "A"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{user?.name || "Admin"}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email || user?.phone}</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto min-h-screen">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

// Dashboard Overview Component
const DashboardOverview = ({ stats, setActiveTab, cities, selectedCity, setSelectedCity }) => {
  return (
    <div data-testid="dashboard-overview">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-foreground">Dashboard Overview</h1>
        
        {/* City Filter */}
        {cities.length > 0 && (
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="w-[180px]" data-testid="city-filter-select">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Active Filter Badge */}
      {selectedCity && selectedCity !== "all" && (
        <div className="mb-4">
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
            <MapPin className="w-3 h-3" />
            Filtered: {cities.find(c => c.id === selectedCity)?.name || selectedCity}
            <button 
              onClick={() => setSelectedCity("all")}
              className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Total Customers"
          value={stats.total_customers}
          icon={Users}
          color="bg-blue-500"
          onClick={() => setActiveTab("customers")}
        />
        <StatCard
          title="Total Drivers"
          value={stats.total_drivers}
          icon={Bike}
          color="bg-green-500"
          onClick={() => setActiveTab("drivers")}
        />
        <StatCard
          title="Online Drivers"
          value={stats.online_drivers}
          icon={TrendingUp}
          color="bg-emerald-500"
        />
        <StatCard
          title="Bike Taxi"
          value={stats.today_bike_taxi}
          icon={Bike}
          color="bg-purple-500"
          onClick={() => setActiveTab("rides")}
        />
        <StatCard
          title="Food Delivery"
          value={stats.today_food_delivery}
          icon={Car}
          color="bg-orange-500"
          onClick={() => setActiveTab("rides")}
        />
        <StatCard
          title="Pending Rides"
          value={stats.pending_rides}
          icon={Clock}
          color="bg-amber-500"
          onClick={() => setActiveTab("rides")}
        />
        <StatCard
          title="Today's Revenue"
          value={`₹${stats.revenue_today?.toLocaleString() || 0}`}
          icon={DollarSign}
          color="bg-primary"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-card rounded-xl border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickActionButton
            label="Manage Cities"
            icon={MapPin}
            onClick={() => setActiveTab("cities")}
          />
          <QuickActionButton
            label="View Drivers"
            icon={Bike}
            onClick={() => setActiveTab("drivers")}
          />
          <QuickActionButton
            label="View Rides"
            icon={Car}
            onClick={() => setActiveTab("rides")}
          />
          <QuickActionButton
            label="Wallet"
            icon={Wallet}
            onClick={() => setActiveTab("wallet")}
          />
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, onClick }) => (
  <button
    onClick={onClick}
    className={`p-4 bg-card rounded-xl border border-border hover:shadow-md transition-shadow ${
      onClick ? "cursor-pointer" : "cursor-default"
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="text-left">
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold text-foreground">{value}</p>
      </div>
    </div>
  </button>
);

// Quick Action Button Component
const QuickActionButton = ({ label, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:bg-muted transition-colors"
  >
    <Icon className="w-6 h-6 text-primary" />
    <span className="text-sm font-medium text-foreground">{label}</span>
  </button>
);

export default AdminDashboard;
